import React from 'react';
import { Box, Grid, Typography, Rating as MuiRating } from '@mui/material';
import { useInfiniteSparksQuery } from 'hooks/spark';
import { InfoCard, SparkDataTable } from 'sharedComponents/UI';
import HistoryCardSkeleton from 'sharedComponents/Skeleton/HistoryCardSkeleton';
import { MarketplaceAccount } from 'types/MarketplaceAccount';
import { Column } from 'sharedComponents/UI/SparkDataTable';
import { formatDateAndTime } from 'utilities/time';
import SparkAvatarBackground from 'sharedComponents/UI/SparkAvatarBackground';
import SparkdefaultIcon from 'components/SVG/SparkdefaultIcon';
import RunSparkModal from 'components/Sparks/Modals/RunSparkModal';
import { useDialog } from 'hooks/common';
import SparkRowOptions from 'components/Sparks/SparkNewContent/SparkRowOptions';
import SparkBadges from 'components/common/SparkBadges';
import { SPARK_BADGE_TYPES } from 'constants/common';
import Pagination from 'sharedComponents/Pagination';

type SparkRow = {
  id: string;
  name: string;
  iconUrl: string;
  updatedAt: string;
  averageRating: string;
  numberOfRatings: string;
  price: string;
  billingTypeName: string;
};

type SparksTabContentProps = {
  marketplaceAccount: MarketplaceAccount | undefined;
};

function SparksTabContent({ marketplaceAccount }: SparksTabContentProps) {
  const marketplaceAccountId = marketplaceAccount?.id;
  const [page, setPage] = React.useState(1);
  const [selectedSparkID, setSelectedSparkID] = React.useState<string>();

  const {
    openDialog: openSparkRunDialog,
    closeDialog: closeSparkRunDialog,
    open: isSparkRunDialogOpen,
  } = useDialog();

  // Fetch sparks associated with this marketplace account
  const {
    joinedSparks,
    hasNextPage,
    isFetching,
    isLoading,
    isFetchingNextPage,
  } = useInfiniteSparksQuery({
    url: marketplaceAccountId ? 
      `sparks?filter[marketplaceAccountID]=${marketplaceAccountId}` : 
      undefined,
  });

  const handleRowClick = (sparkID: string) => {
    setSelectedSparkID(sparkID);
    openSparkRunDialog();
  };

  const columns: Column<SparkRow>[] = [
    {
      field: 'name',
      headerText: 'Name',
      flex: 3,
      sortable: true,
      cellRenderer: (row) => {
        const spark = joinedSparks?.find(
          (joinedSpark) => joinedSpark.id === row.id
        );
        const badge = spark?.meta.badges?.filter(
          (badge) => badge.type === SPARK_BADGE_TYPES.Status
        );
        return (
          <Box display='flex' alignItems='center' gap={2}>
            <SparkAvatarBackground
              src={row.iconUrl || ''}
              icon={<SparkdefaultIcon />}
              width='32px'
              height='32px'
            />
            <Typography
              variant='body1'
              sx={{
                color: '#000000',
                textWrap: 'nowrap',
              }}
            >
              {row.name || '- -'}
            </Typography>
            {!!badge && badge.length > 0 && <SparkBadges badges={badge} />}
          </Box>
        );
      },
    },
    {
      field: 'updatedAt',
      headerText: 'Updated',
      sortable: true,
      cellRenderer: (row) => {
        return (
          <Box display='flex' alignItems='center' gap={2}>
            <Typography
              variant='body1'
              sx={{
                color: '#000000',
              }}
            >
              {row.updatedAt || '- -'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'averageRating',
      headerText: 'Rating',
      sortable: true,
      cellRenderer: (row) => {
        const rating = parseFloat(row.averageRating);
        const count = parseInt(row.numberOfRatings, 10);

        return (
          <Box
            display='flex'
            alignItems='center'
            gap={1}
            sx={{
              "& .MuiRating-root": {
                marginBottom: "3px",
                display: "inline-flex"
              }
            }}
          >
            <MuiRating
              value={rating > 0 ? 1 : 0}
              max={1}
              precision={0.1}
              readOnly
              size="small"
              sx={{
                verticalAlign: "middle",
                fontSize: "20px"
              }}
            />

            <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant='body2'
                component="span"
                sx={{
                  color: '#000000',
                  fontWeight: 'medium',
                  lineHeight: 1
                }}
              >
                {rating > 0 ? rating.toFixed(1) : '0'}
              </Typography>

              <Typography
                variant='body2'
                component="span"
                sx={{
                  color: 'text.secondary',
                  ml: 0.5,
                  lineHeight: 1
                }}
              >
                {count > 0 ? `(${count})` : '(0)'}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'price',
      headerText: 'Price',
      sortable: true,
      cellRenderer: (row) => {
        const price = row.price === 'null' ? null : parseFloat(row.price);
        const spark = joinedSparks?.find((s) => s.id === row.id);
        // Get the billing type ID from the spark attributes
        const billingTypeID = spark?.attributes.sparkBillingTypeID;
        
        // Get the billing type name from the spark's user metadata
        // This is safer than trying to navigate the included data directly
        const billingTypeName = spark?.meta?.user?.usageRights?.billingType || '';

        return (
          <Box display='flex' alignItems='center'>
            <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant='body2'
                component="span"
                sx={{
                  color: '#000000',
                  fontWeight: 'medium',
                  lineHeight: 1
                }}
              >
                {price === null ? 'Free' : `$${price.toFixed(2)}`}
              </Typography>

              {/* Show billing type name when available */}
              {billingTypeName && price !== null && (
                <Typography
                  variant='body2'
                  component="span"
                  sx={{
                    color: 'text.secondary',
                    ml: 0.5,
                    lineHeight: 1
                  }}
                >
                  {`(${billingTypeName})`}
                </Typography>
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: '',
      headerText: '',
      cellRenderer: (row) => {
        const joinedSpark = joinedSparks?.find(
          (joinedSpark) => joinedSpark.id === row.id
        );
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              paddingX: 2,
              width: '100%',
            }}
          >
            <span
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <SparkRowOptions joinedSpark={joinedSpark} />
            </span>
          </Box>
        );
      },
    },
  ];

  const rows: SparkRow[] | undefined = joinedSparks?.map((spark) => {
    return {
      id: spark.id,
      iconUrl: spark?.attributes.iconURL || '',
      name: spark?.attributes.nameforHuman || '',
      updatedAt: formatDateAndTime(spark.attributes.modifiedAt) || '',
      averageRating: String(spark?.meta.averageRating || 0),
      numberOfRatings: String(spark?.meta.numberOfRatings || 0),
      price: String(spark?.attributes.price || 'null'),
      billingTypeName: '', // Will be populated in the cellRenderer
    };
  });

  const totalCount = joinedSparks?.[0]?.totalCount || 0;
  const isPageLoading = isLoading;

  return (
    <Box sx={{ width: '100%' }}>
      {isPageLoading ? (
        <HistoryCardSkeleton />
      ) : joinedSparks && joinedSparks.length > 0 ? (
        <Grid container direction='column' gap={2}>
          <Box display='flex' flexDirection='column' gap={2}>
            <SparkDataTable<SparkRow>
              columns={columns}
              rows={rows || []}
              isRowClickable={true}
              pagination={false}
              onRowClick={handleRowClick}
              showLeftChevron={false}
            />
            {totalCount && totalCount > 10 && (
              <Pagination
                currentPage={page}
                setCurrentPage={setPage}
                dataCount={totalCount || 0}
                itemsPerPage={10}
              />
            )}
          </Box>
        </Grid>
      ) : (
        <Box sx={{ p: 2 }}>
          {marketplaceAccount ? (
            <InfoCard
              info="You don't have any published sparks yet. When you publish sparks, they will appear here."
              type="info"
            />
          ) : (
            <InfoCard
              info="No marketplace account found. Please set up your marketplace account first."
              type="warning"
            />
          )}
        </Box>
      )}
      {isSparkRunDialogOpen && selectedSparkID && (
        <RunSparkModal
          isSparkRunDialogOpen={isSparkRunDialogOpen}
          closeSparkRunDialog={closeSparkRunDialog}
          sparkID={selectedSparkID}
        />
      )}
    </Box>
  );
}

export default SparksTabContent; 